import React, { useState } from 'react';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { Link } from 'react-router-dom';

const EyeIcon = FaEye as unknown as React.FC<{ className?: string }>;
const EyeSlashIcon = FaEyeSlash as unknown as React.FC<{ className?: string }>;

const API_BASE = '/api';

function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [code, setCode] = useState('');
  const [verificationToken, setVerificationToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/auth/login`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || 'Login failed');

      if (data.mfa_required) {
        setVerificationToken(data.verification_token);
      } else {
        window.location.href = '/';
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVerifyMfa = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const res = await fetch(`${API_BASE}/auth/verify-mfa`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ verification_token: verificationToken, code }),
      });
      const data = await res.json();

      if (!res.ok) throw new Error(data.error || 'Verification failed');

      window.location.href = '/';
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <div className="login-image">
          {/* placeholder — swap this for a real site/brand photo when ready */}
          <img
            src="/Galldris-Logo-White-Yellow.png"
            alt="Galldris Group"
            className="login-image-logo"
          />
        </div>

        <div className="login-form-side">
          {!verificationToken ? (
            <>
              <h1 className="login-heading">
                Welcome to<br />Galldris Document Library
              </h1>

              <form onSubmit={handleLogin}>
                <div className="form-field">
                  <label>Email</label>
                  <input
                    type="email"
                    className="login-input"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                  />
                </div>

                <div className="form-field">
                  <label>Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      className="login-input"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeSlashIcon /> : <EyeIcon />}
                    </button>
                  </div>
                </div>

                {error && <p className="error">{error}</p>}

                <button type="submit" className="btn-primary btn-full-width" disabled={submitting}>
                  {submitting ? 'Signing in...' : 'Sign in'}
                </button>

                <Link to="/forgot-password" className="forgot-password-link">Forgot Password?</Link>
              </form>
            </>
          ) : (
            <>
              <h1 className="login-heading">Verify Your Login</h1>
              <p>Enter the verification code sent to your email.</p>

              <form onSubmit={handleVerifyMfa}>
                <div className="form-field">
                  <label>Verification Code</label>
                  <input
                    type="text"
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    required
                  />
                </div>

                {error && <p className="error">{error}</p>}

                <button type="submit" className="btn-primary btn-full-width" disabled={submitting}>
                  {submitting ? 'Verifying...' : 'Verify'}
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default LoginPage;